import React, { useState, useEffect, useRef } from 'react';
import { Play, Award, Briefcase, Users } from 'lucide-react';
import { motion, useInView } from 'framer-motion';
import { BlurFade } from './ui/blur-fade';
import { companyLogos } from '../mockData';

const CountUpAnimation = ({ end, duration = 2 }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  useEffect(() => {
    if (!isInView) return;
    
    let startTime;
    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = (timestamp - startTime) / (duration * 1000);
      
      if (progress < 1) {
        setCount(Math.floor(end * progress));
        requestAnimationFrame(animate);
      } else {
        setCount(end);
      }
    };
    
    requestAnimationFrame(animate);
  }, [isInView, end, duration]);

  return <span ref={ref}>{count}</span>;
};

const AboutTaner = () => {
  const [showVideo, setShowVideo] = useState(false);

  return (
    <section id="hakkimda" className="relative py-24 bg-gradient-to-b from-gray-900 to-black overflow-hidden scroll-mt-20">
      {/* Background Effects */}
      <div className="absolute top-20 left-10 w-96 h-96 bg-purple-600/10 rounded-full filter blur-[150px]"></div>
      <div className="absolute bottom-20 right-10 w-96 h-96 bg-pink-600/10 rounded-full filter blur-[150px]"></div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Video Side - Autoplay */}
          <BlurFade delay={0} inView>
            <div className="relative">
              {/* Autoplay Video */}
              <div className="relative h-[850px] rounded-3xl overflow-hidden shadow-2xl hover:shadow-purple-500/20 transition-all duration-700">
                <video
                  src="/assets/videos/tanertorun-egitim.mp4"
                  autoPlay
                  loop
                  muted
                  playsInline
                  className="w-full h-full object-cover"
                  style={{ objectPosition: 'center top' }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent pointer-events-none"></div>
                
                <div className="absolute bottom-6 left-6">
                  <span className="bg-black/60 backdrop-blur-md text-white px-5 py-2 rounded-full text-sm font-light border border-white/10">
                    Tanıtım Videosu
                  </span>
                </div>
              </div>

              {/* Modern Credentials Cards */}
              <div className="grid grid-cols-3 gap-4 mt-8">
                <BlurFade delay={0.1} inView>
                  <motion.div 
                    className="relative bg-black/40 backdrop-blur-xl border border-white/10 rounded-2xl p-5 text-center group hover:border-purple-400/50 transition-all duration-500"
                    whileHover={{ y: -5, boxShadow: '0 20px 40px rgba(168, 85, 247, 0.15)' }}
                  >
                    <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-gradient-to-br from-purple-500/20 to-pink-500/20 border border-purple-400/30 flex items-center justify-center">
                      <Award className="w-7 h-7 text-purple-300" />
                    </div>
                    <p className="text-white font-bold text-xl mb-1">M.Hyp</p>
                    <p className="text-gray-500 text-xs font-light">Sertifikalı</p>
                  </motion.div>
                </BlurFade>

                <BlurFade delay={0.15} inView>
                  <motion.div 
                    className="relative bg-black/40 backdrop-blur-xl border border-white/10 rounded-2xl p-5 text-center group hover:border-pink-400/50 transition-all duration-500"
                    whileHover={{ y: -5, boxShadow: '0 20px 40px rgba(236, 72, 153, 0.15)' }}
                  >
                    <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-gradient-to-br from-pink-500/20 to-purple-500/20 border border-pink-400/30 flex items-center justify-center">
                      <Briefcase className="w-7 h-7 text-pink-300" />
                    </div>
                    <p className="text-white font-bold text-xl mb-1">
                      <CountUpAnimation end={3} duration={2} />
                    </p>
                    <p className="text-gray-500 text-xs font-light">Şirket</p>
                  </motion.div>
                </BlurFade>

                <BlurFade delay={0.2} inView>
                  <motion.div 
                    className="relative bg-black/40 backdrop-blur-xl border border-white/10 rounded-2xl p-5 text-center group hover:border-blue-400/50 transition-all duration-500"
                    whileHover={{ y: -5, boxShadow: '0 20px 40px rgba(59, 130, 246, 0.15)' }}
                  >
                    <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-gradient-to-br from-blue-500/20 to-purple-500/20 border border-blue-400/30 flex items-center justify-center">
                      <Users className="w-7 h-7 text-blue-300" />
                    </div>
                    <p className="text-white font-bold text-xl mb-1">
                      <CountUpAnimation end={5000} duration={2.5} />+
                    </p>
                    <p className="text-gray-500 text-xs font-light">Katılımcı</p>
                  </motion.div>
                </BlurFade>
              </div>
            </div>
          </BlurFade>

          {/* Content Side */}
          <div className="space-y-8">
            {/* Elegant Badge */}
            <BlurFade delay={0.3} inView>
              <div className="inline-flex items-center gap-3">
                <div className="h-px w-8 bg-gradient-to-r from-transparent to-purple-400"></div>
                <div className="w-1.5 h-1.5 rounded-full bg-purple-400"></div>
                <span className="text-purple-300 font-light text-sm uppercase tracking-[0.2em]">M.Hyp. Taner Torun</span>
              </div>
            </BlurFade>

            {/* Title with Subtle Gradient */}
            <BlurFade delay={0.35} inView>
              <h2 className="text-4xl sm:text-5xl lg:text-6xl font-light text-white leading-tight tracking-tight">
                Zihin Sanatçısı ve{' '}
                <span className="relative inline-block">
                  <span className="relative bg-gradient-to-r from-purple-200 via-purple-300 to-pink-200 bg-clip-text text-transparent">
                    Dönüşüm Mimarı
                  </span>
                  <span className="absolute -bottom-2 left-0 w-full h-px bg-gradient-to-r from-purple-400/0 via-purple-400/60 to-purple-400/0"></span>
                </span>
              </h2>
            </BlurFade>
            
            {/* Opening Paragraph - Larger & More Dramatic */}
            <BlurFade delay={0.4} inView>
              <p className="text-2xl text-gray-300 leading-relaxed italic font-light">
                Her yolculuk, içten gelen bir merakla başlar. Benim yolculuğum, insan zihninin sınırlarını aşan o görünmez gücü keşfetme arzusuyla şekillendi. 
                Zamanla bu arzu, bireyin iç dünyasındaki en narin detaylardan, bir markanın en stratejik hamlesine kadar uzanan, çok katmanlı bir sanata dönüştü.
              </p>
            </BlurFade>

            {/* Content Paragraphs with Stagger */}
            <div className="space-y-6 text-gray-300">
              <BlurFade delay={0.45} inView>
                <p className="text-lg leading-relaxed font-light">
                  Ben, <strong className="text-white font-normal">bilinçaltının dilini çözen bir zihin bilimci</strong> ve aynı zamanda <strong className="text-white font-normal">dijital dünyada fark yaratan bir iş insanıyım</strong>. 
                  Kurucusu olduğum <strong className="text-purple-300 font-normal">Medyatik</strong> çatısı altında, yazılımın mantığını nöropazarlamanın duygusal gücüyle birleştirerek, 
                  markaların sadece görünmesini değil, hedef kitlenin kalbine dokunmasını sağlayan stratejiler inşa ediyorum.
                </p>
              </BlurFade>

              <BlurFade delay={0.5} inView>
                <p className="text-lg leading-relaxed font-light">
                  <strong className="text-purple-300 font-normal">MindLab Academy</strong>'de ise, eğitim bir bilgi aktarımı değil; bir uyanıştır. 
                  Katılımcı, kendi içindeki en güçlü yazılımı keşfeder, eski kodları sevgiyle bırakır ve arzuladığı geleceğin haritasını yeniden çizer.
                </p>
              </BlurFade>

              {/* Mission Statement with Divider */}
              <BlurFade delay={0.55} inView>
                <div className="pt-6 border-t border-white/10">
                  <p className="text-xl leading-relaxed">
                    <strong className="text-white font-normal">Misyonum:</strong>{' '}
                    <span className="italic text-gray-200">Stratejiye ruh katmak, zihne ise kalıcı bir ihtişam kazandırmaktır.</span>
                  </p>
                </div>
              </BlurFade>
            </div>

            {/* Companies - Modern Glassmorphism Cards */}
            <BlurFade delay={0.6} inView>
              <div className="pt-8">
                <div className="flex items-center gap-3 mb-8">
                  <p className="text-gray-500 text-xs uppercase tracking-[0.2em] font-light">Şirketler</p>
                  <div className="h-px flex-1 bg-gradient-to-r from-gray-700 to-transparent"></div>
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {companyLogos.map((company, index) => (
                    <BlurFade key={company.id} delay={0.65 + index * 0.05} inView>
                      <motion.div 
                        className="relative bg-black/30 backdrop-blur-xl border border-white/10 rounded-2xl p-6 group hover:bg-black/40 transition-all duration-500"
                        whileHover={{ 
                          y: -3, 
                          boxShadow: '0 20px 40px rgba(168, 85, 247, 0.1)',
                          borderColor: 'rgba(168, 85, 247, 0.3)'
                        }}
                      >
                        {/* Subtle Glow on Hover */}
                        <div className="absolute -inset-px bg-gradient-to-r from-purple-600/0 via-purple-600/10 to-pink-600/0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-xl"></div>
                        
                        <div className="relative">
                          <p className="text-white font-semibold text-sm mb-2">{company.name}</p>
                          <p className="text-gray-400 text-xs leading-relaxed font-light">{company.description}</p>
                        </div>
                      </motion.div>
                    </BlurFade>
                  ))}
                </div>
              </div>
            </BlurFade>
          </div>
        </div>
      </div>

      {/* Video Modal */}
      {showVideo && (
        <div 
          className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4"
          onClick={() => setShowVideo(false)}
        >
          <div className="relative max-w-5xl w-full aspect-video bg-gray-900 rounded-lg overflow-hidden">
            <button
              onClick={() => setShowVideo(false)}
              className="absolute top-4 right-4 bg-white/10 hover:bg-white/20 text-white p-2 rounded-full z-10"
              aria-label="Videoyu kapat"
            >
              ✕
            </button>
            <div className="w-full h-full flex items-center justify-center text-white">
              <p className="text-xl">Video oynatıcı buraya entegre edilecek</p>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default AboutTaner;